/**
 * schedule.js — the date engine.
 *
 * Pure: no DOM, no globals, no side effects. Everything it needs arrives as an
 * argument, so it can be unit-tested under `node --test` (see test/schedule.test.mjs).
 */

import { CDN_BASE, DEFAULTS } from './config.js';

const MS_PER_DAY = 86400000;

/**
 * Calendar-day number for a Date, using its *local* Y/M/D.
 *
 * Date.UTC on local components gives a stable integer per calendar day that is
 * immune to DST and timezone offset. Day arithmetic is then plain integer math
 * instead of millisecond juggling.
 */
export function dayIndex(date) {
  return Math.floor(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) / MS_PER_DAY);
}

/**
 * Build a local-midnight Date from calendar parts.
 *
 * Deliberately never `new Date(string)`: an ISO-looking string is parsed as UTC
 * while a non-ISO one is parsed as local, which is exactly how the old popup.js
 * drifted a day in western timezones.
 */
function localDate(year, month, day) {
  const d = new Date(year, month - 1, day);
  return isNaN(d.getTime()) ? null : d;
}

/** Resolve one event to a concrete Date for the given year (null year = fixed date). */
function occurrence(ev, year) {
  if (ev.recur === 'annual') {
    const m = /^(\d{1,2})-(\d{1,2})$/.exec(String(ev.date || '').trim());
    if (!m) return null;
    return localDate(year, +m[1], +m[2]);
  }
  const m = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(String(ev.date || '').trim());
  if (!m) return null;
  return localDate(+m[1], +m[2], +m[3]);
}

/** `*` matches everything, `*.example.com` matches the apex and any subdomain, anything else is exact. */
function hostMatches(pattern, hostname) {
  const p = String(pattern || '').trim().toLowerCase();
  const h = String(hostname || '').trim().toLowerCase();
  if (!p) return false;
  if (p === '*') return true;
  if (p.startsWith('*.')) {
    const base = p.slice(2);
    return h === base || h.endsWith('.' + base);
  }
  return h === p;
}

/** No `audience` block means "every portal". */
function hostAllowed(audience, hostname) {
  if (!audience) return true;
  const exclude = audience.exclude;
  if (Array.isArray(exclude) && exclude.some((p) => hostMatches(p, hostname))) return false;
  const hosts = audience.hosts;
  if (!Array.isArray(hosts) || hosts.length === 0) return true;
  return hosts.some((p) => hostMatches(p, hostname));
}

/** Absolute URLs and protocol-relative URLs pass through; anything else hangs off the CDN. */
export function assetUrl(path) {
  const p = String(path || '').trim();
  if (!p) return '';
  if (/^(https?:)?\/\//i.test(p)) return p;
  return CDN_BASE.replace(/\/+$/, '') + '/' + p.replace(/^\/+/, '');
}

function pick(value, fallback) {
  return typeof value === 'number' && isFinite(value) ? value : fallback;
}

/** Flatten an event + the calendar defaults into the shape render.js consumes. */
function normalize(ev, defaults, occursOn) {
  const theme = ev.theme || {};
  const gradient = Array.isArray(theme.gradient) && theme.gradient.length > 1 ? theme.gradient : null;
  return {
    id: String(ev.id),
    title: typeof ev.title === 'string' ? ev.title : '',
    img: assetUrl(ev.img),
    alt: typeof ev.alt === 'string' ? ev.alt : (typeof ev.title === 'string' ? ev.title : ''),
    link: typeof ev.link === 'string' && ev.link ? ev.link : null,
    width: pick(theme.width, null),
    offset: pick(theme.offset, null),
    titleColor: typeof theme.titleColor === 'string' ? theme.titleColor : null,
    gradient: gradient,
    // Opt out per event with "petals": false; festive by default.
    petals: theme.petals === false ? false : true,
    position: typeof ev.position === 'string' ? ev.position : defaults.position,
    snoozeHours: pick(ev.snoozeHours, defaults.snoozeHours),
    priority: pick(ev.priority, defaults.priority),
    occursOn: occursOn,
  };
}

/**
 * Which event — if any — should be on screen right now.
 *
 * @param {object} config    parsed events.json
 * @param {Date}   now       current time (injectable, so QA can simulate a date)
 * @param {string} hostname  location.hostname
 * @returns {object|null}    a normalized event, or null
 */
export function resolveActive(config, now, hostname) {
  const defaults = Object.assign({}, DEFAULTS, (config && config.defaults) || {});
  const events = config && Array.isArray(config.events) ? config.events : [];
  const today = dayIndex(now);
  const thisYear = now.getFullYear();

  let best = null;

  for (const ev of events) {
    if (!ev || typeof ev !== 'object') continue;
    if (ev.enabled === false) continue;
    if (!ev.id) continue;
    if (!ev.img && !ev.title) continue;
    if (!hostAllowed(ev.audience, hostname)) continue;

    const before = pick(ev.windowBefore, defaults.windowBefore);
    const after = pick(ev.windowAfter, defaults.windowAfter);

    // Adjacent years too, so a 31-Dec event with a +1 day window still matches on 1 Jan.
    const years = ev.recur === 'annual' ? [thisYear - 1, thisYear, thisYear + 1] : [null];

    for (const year of years) {
      const occ = occurrence(ev, year);
      if (!occ) continue;
      const idx = dayIndex(occ);
      if (today < idx - before || today > idx + after) continue;

      const candidate = normalize(ev, defaults, occ);
      // Strict `>` keeps the earlier entry when priorities tie.
      if (!best || candidate.priority > best.priority) best = candidate;
      break;
    }
  }

  return best;
}
